import { Component, JSX, createEffect, createMemo, createResource, createSignal, onCleanup, onMount, Show } from "solid-js";
import { useLocation, A } from "@solidjs/router";
import { LeftSidebar, SidebarSection } from "./LeftSidebar";
import { TopHeader } from "./TopHeader";
import { FeedbackWidget } from "./FeedbackWidget";
import { Breadcrumb } from "./Breadcrumb";
import { useBranding } from "./BrandingProvider";
import { api } from "../utils/apiClient";
import { useAuth } from "../store/authStore";
import { trace } from "../lib/trace";

interface DashboardLayoutProps {
  children: JSX.Element;
}

interface AccountInfo {
  id: string;
  name: string;
  status: string;
}

/** Sentinel: tracks whether the last account-info fetch failed. */
let _accountFetchFailed = false;

const fetchAccountInfo = async (accountId: string): Promise<AccountInfo | null> => {
  if (!accountId || accountId === "new") { _accountFetchFailed = false; return null; }
  trace.api("DashboardLayout: fetching account info", { accountId });
  try {
    const data = await api.get<AccountInfo>(`/api/clients/${accountId}`);
    trace.api("DashboardLayout: account info loaded", { accountId, name: data.name });
    _accountFetchFailed = false;
    return data;
  } catch (err: unknown) {
    trace.api("DashboardLayout: account info error", { accountId, error: String(err) }, "error");
    _accountFetchFailed = true;
    return null;
  }
};

/**
 * OfflineBanner - shows a small bar at the top of the page when the browser
 * loses its network connection.  Listens to the "online" / "offline" events
 * and checks `navigator.onLine` on mount.
 */
const OfflineBanner: Component = () => {
  const [isOffline, setIsOffline] = createSignal(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );

  onMount(() => {
    const goOffline = () => {
      setIsOffline(true);
      trace.lifecycle("Network offline detected");
    };
    const goOnline = () => {
      setIsOffline(false);
      trace.lifecycle("Network reconnected");
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);

    onCleanup(() => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    });
  });

  return (
    <Show when={isOffline()}>
      <div
        role="alert"
        aria-live="assertive"
        class="offline-banner"
        style={{
          position: "fixed",
          top: "0",
          left: "0",
          right: "0",
          "z-index": "9999",
          background: "var(--warning, #f59e0b)",
          color: "#1a1a1a",
          "text-align": "center",
          padding: "6px 16px",
          "font-size": "13px",
          "font-weight": "500",
          display: "flex",
          "align-items": "center",
          "justify-content": "center",
          gap: "8px",
        }}
      >
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        You are currently offline. Some features may be unavailable.
      </div>
    </Show>
  );
};

export const DashboardLayout: Component<DashboardLayoutProps> = (props) => {
  const location = useLocation();
  const brandingCtx = useBranding();
  const { isAdmin, userRole } = useAuth();

  trace.navigation("DashboardLayout mounted", { path: location.pathname });

  // Set document.title based on current route for screen readers and browser tabs
  const routeTitles: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/accounts": "Accounts",
    "/clients": "Accounts",
    "/fleet/instances": "Fleet Instances",
    "/fleet/provision": "Fleet Provisioning",
    "/fleet/updates": "Update Campaigns",
    "/fleet": "Fleet Overview",
    "/gateways": "Gateways",
    "/bundles": "Bundle Factory",
    "/credentials": "Credentials",
    "/user-management": "User Management",
    "/notifications": "Notifications",
    "/diagnostics": "Diagnostics",
    "/components": "Component Versions",
    "/health": "Platform Health",
    "/observability": "Observability",
    "/audit": "Platform Audit",
    "/sessions": "Active Sessions",
    "/roles": "Role Templates",
    "/admin/dashboard": "Admin Dashboard",
    "/admin/reconciliation": "Reconciliation",
    "/settings": "Settings",
    "/cache-rules": "Cache Rules",
    "/cache": "Cache Overview",
    "/analytics": "Query Analytics",
    "/deployments": "Deployments",
    "/local/localstack": "LocalStack",
    "/local/azurite": "Azurite",
    "/local/tunnels": "Tunnels",
    "/local/secrets": "Provider Secrets",
    "/local/resources": "Cloud Resources",
    "/local/devcontainer": "Dev Container",
  };

  createEffect(() => {
    const path = location.pathname;
    // Longer prefixes first so /fleet/instances wins over /fleet
    const sortedKeys = Object.keys(routeTitles).sort((a, b) => b.length - a.length);
    const match = sortedKeys.find((k) => path === k || path.startsWith(k + "/"));
    const brandName = brandingCtx.branding().brandName || "AirBrx";
    document.title = match ? `${routeTitles[match]} \u2014 ${brandName}` : `${brandName} Control Plane`;
  });

  // Detect if we're in an account context
  const accountId = createMemo(() => {
    const match = location.pathname.match(/\/clients\/([^/]+)/);
    const id = match ? match[1] : null;
    trace.navigation("DashboardLayout: account context", { accountId: id, path: location.pathname });
    return id;
  });

  // Fetch account info when in account context
  const [accountFetchFailed, setAccountFetchFailed] = createSignal(false);
  const [accountInfo] = createResource(accountId, async (id) => {
    const result = await fetchAccountInfo(id);
    setAccountFetchFailed(_accountFetchFailed);
    return result;
  });

  // Platform-level navigation (SaaS admin view)
  const platformNavigation: SidebarSection[] = [
    {
      title: "Proxy Fleet",
      adminOnly: true,
      items: [
        { path: "/fleet", label: "Fleet Overview", icon: "fleet" },
        { path: "/fleet/instances", label: "All Instances", icon: "instances" },
        { path: "/fleet#proxies", label: "Proxy Management", icon: "proxy" },
        { path: "/gateways", label: "Gateways", icon: "gateway" },
      ],
    },
    {
      title: "Platform",
      items: [
        { path: "/dashboard", label: "Dashboard", icon: "dashboard" },
        { path: "/accounts", label: "Accounts", icon: "accounts" },
        { path: "/credentials", label: "Credentials", icon: "credentials", adminOnly: true },
        { path: "/user-management", label: "User Management", icon: "users" },
        { path: "/billing/usage", label: "Usage Analytics", icon: "analytics" },
        { path: "/notifications", label: "Notifications", icon: "notifications" },
      ],
    },
    {
      title: "Operations",
      adminOnly: true,
      items: [
        { path: "/bundles", label: "Bundle Factory", icon: "bundle" },
        { path: "/diagnostics", label: "Diagnostics", icon: "diagnostics" },
        { path: "/components", label: "Component Versions", icon: "components", adminOnly: true },
      ],
    },
    {
      title: "Platform Admin",
      adminOnly: true,
      items: [
        { path: "/health", label: "Platform Health", icon: "health" },
        { path: "/observability", label: "Observability", icon: "observability" },
        { path: "/audit", label: "Platform Audit", icon: "audit" },
        { path: "/sessions", label: "Active Sessions", icon: "sessions" },
        { path: "/roles", label: "Role Templates", icon: "roles" },
        { path: "/admin/dashboard", label: "Admin Dashboard", icon: "home" },
        { path: "/admin/reconciliation", label: "Reconciliation", icon: "sync" },
      ],
    },
  ];

  // Account-level navigation (when viewing a specific account)
  const getAccountNavigation = (accId: string): SidebarSection[] => {
    const sections: SidebarSection[] = [];

    // Add Admin Dashboard link at the top for admin users
    if (isAdmin()) {
      sections.push({
        title: "",
        items: [
          { path: "/dashboard", label: "Admin Dashboard", icon: "home" },
        ],
        adminOnly: true,
      });
    }

    sections.push(
      {
        title: "Account",
        items: [
          { path: `/clients/${accId}`, label: "Dashboard", icon: "dashboard" },
          { path: `/clients/${accId}/proxies`, label: "Proxies", icon: "proxy" },
          { path: `/clients/${accId}/gateways`, label: "Gateways", icon: "gateway" },
          { path: `/clients/${accId}/deployments`, label: "Deployments", icon: "deployments" },
        ],
      },
      {
        title: "Access",
        items: [
          { path: `/clients/${accId}/users`, label: "Users", icon: "users" },
          { path: `/clients/${accId}/credentials`, label: "Credentials", icon: "credentials" },
          { path: `/clients/${accId}/api-keys`, label: "API Keys", icon: "key" },
        ],
      },
      {
        title: "Monitoring",
        items: [
          { path: `/clients/${accId}/audit`, label: "Audit Logs", icon: "audit" },
          { path: `/clients/${accId}/logs`, label: "Logs", icon: "logs" },
        ],
      },
      {
        title: "Settings",
        items: [
          { path: `/clients/${accId}/settings`, label: "Account Settings", icon: "settings" },
        ],
      }
    );

    return sections;
  };

  // Determine which navigation to show
  const navigationSections = createMemo(() => {
    const accId = accountId();
    if (accId) {
      return getAccountNavigation(accId);
    }
    return platformNavigation;
  });

  // Account context for sidebar header
  const accountContext = createMemo(() => {
    const accId = accountId();
    const info = accountInfo();
    if (accId && info) {
      return { id: accId, name: info.name };
    }
    return null;
  });

  return (
    <div class="dashboard-layout" style={{ display: "flex", "min-height": "100vh" }}>
      {/* Offline connectivity banner */}
      <OfflineBanner />

      {/* Skip to content link for keyboard/screen reader users */}
      <a href="#main-content" class="skip-link">
        Skip to main content
      </a>

      {/* Left Sidebar */}
      <LeftSidebar sections={navigationSections()} accountContext={accountContext()} />

      {/* Main Content Area */}
      <div
        class="main-content"
        style={{
          flex: "1",
          "margin-inline-start": "260px",
          "margin-top": "64px",
          "min-height": "calc(100vh - 64px)",
          transition: "margin-inline-start 0.3s ease",
        }}
      >
        {/* Top Header */}
        <TopHeader />

        {/* Account Context Banner — hide for /clients/new (create page) */}
        <Show when={accountId() && accountId() !== "new"}>
          <div
            class="account-banner--sticky"
            style={{
              background: "var(--bg-secondary)",
              "border-bottom": "1px solid var(--border-medium)",
              padding: "12px 24px",
              display: "flex",
              "align-items": "center",
              "justify-content": "space-between",
            }}
          >
            <div style={{ display: "flex", "align-items": "center", gap: "12px" }}>
              <A
                href="/accounts"
                style={{
                  color: "var(--text-secondary)",
                  "text-decoration": "none",
                  "font-size": "14px",
                }}
              >
                All Accounts
              </A>
              <span style={{ color: "var(--text-secondary)" }}>/</span>
              <div style={{ display: "flex", "align-items": "center", gap: "8px" }}>
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    "border-radius": "8px",
                    background: "var(--brand-primary)",
                    display: "flex",
                    "align-items": "center",
                    "justify-content": "center",
                    color: "white",
                    "font-weight": "600",
                    "font-size": "14px",
                  }}
                >
                  {accountInfo()?.name?.charAt(0).toUpperCase() || (accountFetchFailed() ? "!" : "A")}
                </div>
                <div>
                  <div style={{ "font-weight": "600", color: accountFetchFailed() ? "var(--error, #ef4444)" : "var(--text-primary)" }}>
                    {accountInfo()?.name || (accountFetchFailed() ? "Unknown Account" : "Loading...")}
                  </div>
                  <div style={{ "font-size": "12px", color: "var(--text-secondary)" }}>
                    Account ID: {accountId()}
                  </div>
                </div>
                <Show when={userRole()}>
                  <span class={`csw-role-pill csw-role-pill--${userRole()?.toLowerCase().replace(/\s+/g, "-")}`}>
                    {userRole()}
                  </span>
                </Show>
              </div>
            </div>
            <Show when={isAdmin()}>
              <A
                href="/accounts"
                style={{
                  padding: "6px 12px",
                  "border-radius": "6px",
                  background: "var(--bg-primary)",
                  border: "1px solid var(--border-medium)",
                  color: "var(--text-primary)",
                  "text-decoration": "none",
                  "font-size": "13px",
                  display: "flex",
                  "align-items": "center",
                  gap: "6px",
                }}
              >
                <span>←</span> Switch Account
              </A>
            </Show>
          </div>
        </Show>

        {/* Page Content */}
        <main
          id="main-content"
          style={{
            padding: "24px",
            "max-width": "100%",
            width: "100%",
          }}
        >
          {/* Breadcrumb navigation */}
          <Breadcrumb />

          {props.children}
        </main>
      </div>

      {/* Feedback Widget */}
      <FeedbackWidget />

      {/* Responsive styles now in global.css */}
    </div>
  );
};
